import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalSearchParams } from "expo-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { authApi, mapAuthError } from "@/lib/api/auth";
import { registerSchema, type RegisterValues } from "../schemas/authSchemas";
import type { AuthUser as ContextAuthUser } from "@/context/AuthContext";
import type { AuthUser as ApiAuthUser } from "@chinese-vendor/schemas";
import { toast } from "@/lib/toast";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

function toContextUser(apiUser: ApiAuthUser): ContextAuthUser {
  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    roles: apiUser.roles,
    vendorStatus: apiUser.vendor_status,
    applicationId: apiUser.application_id ?? null,
    temporaryPasswordSet: apiUser.temporary_password_set ?? false,
  };
}

export function useRegisterForm() {
  const { t } = useI18n();
  const { login } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();
  const inviteToken = params.token ?? "";
  const decodedInviteToken = inviteToken ? decodeURIComponent(inviteToken) : inviteToken;

  // Validate the invite token server-side and retrieve the associated email.
  const inviteQuery = useQuery({
    queryKey: ["verify-invite-token", decodedInviteToken],
    queryFn: () => authApi.verifyInviteToken(decodedInviteToken),
    enabled: !!decodedInviteToken,
    retry: false,
  });

  const inviteEmail =
    inviteQuery.data?.valid === true && inviteQuery.data.email ? inviteQuery.data.email : "";
  const isInviteInvalid =
    !inviteToken ||
    inviteQuery.isLoading ||
    inviteQuery.isError ||
    (inviteQuery.isSuccess && !inviteQuery.data?.valid);

  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
    },
  });

  // Sync the read-only email field once the invite query resolves.
  useEffect(() => {
    if (inviteEmail) {
      form.setValue("email", inviteEmail, { shouldValidate: false });
    }
  }, [inviteEmail, form]);

  const registerMutation = useMutation({
    mutationFn: (values: RegisterValues) =>
      authApi.register({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        password: values.password,
      }),
    onSuccess: async (result) => {
      await login(result.token, toContextUser(result.user));
    },
  });

  const handleSubmit = isInviteInvalid
    ? () => {
        toast.error({
          message: t("auth.register.error.invalidInvite"),
        });
      }
    : form.handleSubmit(async (values) => {
        await registerMutation.mutateAsync(values).catch(() => {});
      });

  const error = registerMutation.isError ? mapAuthError(registerMutation.error) : null;

  useEffect(() => {
    if (!error) return;

    toast.error({
      message: t(error as TranslationKeys),
    });
  }, [error, t]);

  return {
    form,
    isLoading: registerMutation.isPending,
    isVerifyingInvite: inviteQuery.isLoading,
    error,
    handleSubmit,
    email: inviteEmail,
    isInviteInvalid,
  };
}
